// İlk önce CSS import
import '../../../context/AuthContext.css';

// Sonra React ve diğer kütüphane importları
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import AccountSwitcher from '../../../components/AccountSwitcher';
import DateRangeFilter from '../../../components/filters/DateRangeFilter';
import { formatDateYMD } from '../../../utils/dateRangeHelpers';
import Ga4WebOnlyNote from '../../../components/Ga4WebOnlyNote';
import { useAccounts } from '../../../context/AccountContext';
import { Card } from '../../../components/ui/Card';
import {
  Filter,
  Download,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronRight,
  LayoutList,
  TrendingUp,
  DollarSign,
  MousePointerClick,
  ShoppingCart,
  Calendar,
  Check,
  Loader2,
  Edit2,
  X,
  AlertTriangle,
  Activity,
  History,
  Clock,
  XCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Sample chart data
const chartData = {
  labels: ['1 Jan', '2 Jan', '3 Jan', '4 Jan', '5 Jan', '6 Jan', '7 Jan'],
  datasets: [
    {
      label: 'Spend',
      data: [300, 450, 320, 500, 350, 200, 400],
      borderColor: 'rgb(59, 130, 246)',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      fill: true,
      tension: 0.4,
      yAxisID: 'y',
    },
    {
      label: 'Revenue',
      data: [500, 300, 400, 600, 450, 350, 550],
      borderColor: 'rgb(16, 185, 129)',
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      fill: true,
      tension: 0.4,
      yAxisID: 'y',
    }
  ]
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: {
      type: 'linear',
      display: true,
      position: 'left',
      beginAtZero: true,
      grid: {
        color: 'rgba(0, 0, 0, 0.05)',
      },
      ticks: {
        callback: (value) => {
          if (value >= 1000000) {
            return `₺${(value / 1000000).toFixed(1)}M`;
          } else if (value >= 1000) {
            return `₺${(value / 1000).toFixed(1)}K`;
          }
          return `₺${value}`;
        },
        font: {
          size: 11
        }
      }
    },
    y1: {
      type: 'linear',
      display: true,
      position: 'right',
      beginAtZero: true,
      grid: {
        drawOnChartArea: false,
      },
      ticks: {
        callback: (value) => {
          if (value >= 1000000) {
            return `${(value / 1000000).toFixed(1)}M`;
          } else if (value >= 1000) {
            return `${(value / 1000).toFixed(1)}K`;
          }
          return value.toString();
        },
        font: {
          size: 11
        }
      }
    },
    x: {
      grid: {
        display: false
      },
      ticks: {
        maxTicksLimit: 10,
        font: {
          size: 11
        }
      }
    }
  },
  plugins: {
    legend: {
      position: 'top',
      align: 'end',
      labels: {
        boxWidth: 12,
        usePointStyle: true,
        pointStyle: 'circle',
        padding: 15,
        font: {
          size: 12
        }
      }
    },
    tooltip: {
      callbacks: {
        label: function(context) {
          let label = context.dataset.label || '';
          if (label) {
            label += ': ';
          }
          if (context.parsed.y !== null) {
            if (context.dataset.label === 'Cost') {
              label += `₺${context.parsed.y.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
            } else {
              label += context.parsed.y.toLocaleString('tr-TR');
            }
          }
          return label;
        }
      }
    }
  },
  interaction: {
    intersect: false,
    mode: 'index',
  }
};

export default function DynamicSearch() {
  const { selectedBrand } = useAuth();
  const { getSelectedAccountId } = useAccounts();
  const selectedAccountId = getSelectedAccountId('GOOGLE_ADS');

  // Live status/budget overlay (fetched from Google Ads API)
  const [liveDataMap, setLiveDataMap] = useState({});
  const [isLoadingLive, setIsLoadingLive] = useState(false);

  // Inline status/budget editing state
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null);
  const [editBudget, setEditBudget] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  // Change History state
  const [showChangeHistory, setShowChangeHistory] = useState(false);
  const [changeHistoryData, setChangeHistoryData] = useState([]);
  const [changeHistoryTotal, setChangeHistoryTotal] = useState(0);
  const [changeHistoryLoading, setChangeHistoryLoading] = useState(false);
  const [changeHistoryFilter, setChangeHistoryFilter] = useState('all');

  const [isLoading, setIsLoading] = useState(false);
  const [dateRange, setDateRange] = useState('last30');
  const [gaAccountFilter, setGaAccountFilter] = useState('all');
  const [gaAccounts, setGaAccounts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [expandedCampaigns, setExpandedCampaigns] = useState(new Set());
  const [expandedAdGroups, setExpandedAdGroups] = useState(new Set());
  // Sıralama için state değişkenleri
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Custom date picker için state'leri başa taşıyoruz
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  // Chart verisi için state
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Spend',
        data: [],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y',
      },
      {
        label: 'Revenue',
        data: [],
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y',
      }
    ]
  });

  // Add missing state variables
  const [trendData, setTrendData] = useState([]);
  const [isLoadingTrend, setIsLoadingTrend] = useState(false);

  // Update the state to include the new filter option
  const [selectedDateRange, setSelectedDateRange] = useState('7days');

  // State kısmına dataLoading ekleyelim
  const [dataLoading, setDataLoading] = useState(true);

  // DSA Landing Page verisi için state'ler
  const [dsaLandingPageData, setDsaLandingPageData] = useState(null);
  const [isLoadingDsaData, setIsLoadingDsaData] = useState(false);

  // Helper function to sort keywords by field and direction
  const sortKeywords = (keywords, field, direction) => {
    if (!keywords || !Array.isArray(keywords) || !field) return keywords;
    return [...keywords].sort((a, b) => {
      if (['spend', 'budget', 'impressions', 'clicks', 'ctr', 'cpc', 'conversions', 'conversionRate', 'costPerConversion', 'conversionValue', 'roas'].includes(field)) {
        const aVal = parseFloat(a[field]) || 0;
        const bVal = parseFloat(b[field]) || 0;
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const aVal = a[field] || 0;
      const bVal = b[field] || 0;
      return direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  // Kampanya genişletme/daraltma işlevi
  const toggleCampaign = (campaignId, e) => {
    e.stopPropagation();
    setExpandedCampaigns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(campaignId)) {
        newSet.delete(campaignId);
      } else {
        newSet.add(campaignId);
      }
      return newSet;
    });
  };

  // Reklam grubu genişletme/daraltma işlevi
  const toggleAdGroup = (adGroupId, e) => {
    e.stopPropagation();
    setExpandedAdGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(adGroupId)) {
        newSet.delete(adGroupId);
      } else {
        newSet.add(adGroupId);
      }
      return newSet;
    });
  };

  // Sıralama işlevi
  const handleSort = (field) => {
    if (sortField === field) {
      // Aynı alan üzerine tıklandığında sıralama yönünü değiştir
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Yeni bir alan seçildiğinde, varsayılan olarak azalan sıralama yap
      setSortField(field);
      setSortDirection(field === 'budget' ? 'desc' : 'asc');
    }
  };

  // Calculate summary metrics from campaigns
  const calculateSummaryMetrics = (campaigns) => {
    if (!campaigns || campaigns.length === 0) {
      return {
        cpc: 0,
        conversionRate: 0,
        cost: 0,
        roas: 0
      };
    }

    let totalClicks = 0;
    let totalCost = 0;
    let totalConversions = 0;
    let totalRevenue = 0;

    campaigns.forEach(campaign => {
      totalClicks += campaign.clicks || 0;
      totalCost += campaign.spend || 0;
      totalConversions += campaign.conversions || 0;
      totalRevenue += campaign.revenue || 0;
    });

    const avgCpc = totalClicks > 0 ? totalCost / totalClicks : 0;
    const conversionRate = totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0;
    const roas = totalCost > 0 ? totalRevenue / totalCost : 0;

    return {
      cpc: avgCpc,
      conversionRate: conversionRate,
      cost: totalCost,
      roas: roas
    };
  };

  // DSA Landing Page verilerini çeken fonksiyon
  const fetchDsaLandingPageData = async () => {
    try {
      setIsLoadingDsaData(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        console.error('No brand ID found in localStorage');
        return;
      }

      console.log(`Fetching DSA landing page data for brand ID: ${brandId} with date range: ${dateRange}`);

      // Custom date range için URL parametrelerini ekle
      let apiUrl = `https://api.glowytics.com/api/google-ads/dsa-landing-pages-raw?dateRange=${dateRange}${gaAccountFilter !== 'all' ? '&accountId=' + gaAccountFilter : ''}`;
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        const startDateStr = formatDateYMD(customStartDate);
        const endDateStr = formatDateYMD(customEndDate);
        apiUrl += `&startDate=${startDateStr}&endDate=${endDateStr}`;
      }

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('DSA Landing Page API Error:', errorData);
        throw new Error(`Failed to fetch DSA landing page data: ${errorData.message || response.statusText}`);
      }

      const data = await response.json();
      
      console.log('DSA landing page data fetched successfully:', data);
      console.log('Landing page summary:', data?.landingPageSummary);
      console.log('Total metrics:', data?.totalMetrics);
      console.log('Campaign summary:', data?.campaignSummary);
      
      // Metrik değerlerini debug et
      if (data?.totalMetrics) {
        console.log('Total cost from backend:', data.totalMetrics.total_cost);
        console.log('Total clicks from backend:', data.totalMetrics.total_clicks);
        console.log('Total impressions from backend:', data.totalMetrics.total_impressions);
        console.log('Overall CPC from backend:', data.totalMetrics.overall_cpc);
        console.log('Overall CTR from backend:', data.totalMetrics.overall_ctr);
      }
      
      // Landing page verilerini daha detaylı log'la
      if (data?.landingPageSummary && data.landingPageSummary.length > 0) {
        console.log('First landing page record:', data.landingPageSummary[0]);
        console.log('Landing page URL field:', data.landingPageSummary[0]?.landing_page_url);
      }
      
      // Save DSA landing page data to state
      setDsaLandingPageData(data);
      
      // Update chart with daily trend data
      if (data && data.dailyTrend && data.dailyTrend.length > 0) {
        updateDsaChart(data.dailyTrend);
      } else {
        // Clear chart if no data
        console.log('No DSA landing page data found - clearing chart');
        updateDsaChart([]);
      }
      
    } catch (error) {
      console.error('Error fetching DSA landing page data:', error);
      // Set empty data on error
      setDsaLandingPageData({
        rawData: [],
        landingPageSummary: [],
        campaignSummary: [],
        dailyTrend: [],
        totalMetrics: {
          total_impressions: 0,
          total_clicks: 0,
          total_cost: 0,
          unique_landing_pages: 0,
          unique_campaigns: 0,
          overall_ctr: 0,
          overall_cpc: 0
        }
      });
      
      // Clear chart on error
      console.log('Error occurred - clearing chart');
      updateDsaChart([]);
    } finally {
      setIsLoadingDsaData(false);
    }
  };

  // DSA chart güncelleme fonksiyonu
  const updateDsaChart = (dailyTrendData) => {
    if (!dailyTrendData || dailyTrendData.length === 0) {
      console.log("No DSA daily trend data available - clearing chart");
      // Clear chart by setting empty data
      const emptyChartData = {
        labels: [],
        datasets: [
          {
            label: 'Cost',
            data: [],
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: false,
            tension: 0.4,
            yAxisID: 'y'
          },
          {
            label: 'Clicks',
            data: [],
            borderColor: 'rgb(16, 185, 129)',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            fill: false,
            tension: 0.4,
            yAxisID: 'y1'
          },
          {
            label: 'Impressions',
            data: [],
            borderColor: 'rgb(249, 115, 22)',
            backgroundColor: 'rgba(249, 115, 22, 0.1)',
            fill: false,
            tension: 0.4,
            yAxisID: 'y1'
          }
        ]
      };
      setChartData(emptyChartData);
      console.log("DSA chart cleared");
      return;
    }
    
    // Tarihleri formatlayarak etiketleri oluştur
    const labels = dailyTrendData.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
    });
    
    // Maliyet, tıklama ve gösterim değerlerini çıkar
    const costs = dailyTrendData.map(item => item.cost || 0);
    const clicks = dailyTrendData.map(item => item.clicks || 0);
    const impressions = dailyTrendData.map(item => item.impressions || 0);
    
    // Grafik verilerini güncelle
    const newChartData = {
      labels: labels,
      datasets: [
        {
          label: 'Cost',
          data: costs,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: false,
          tension: 0.4,
          yAxisID: 'y'
        },
        {
          label: 'Clicks',
          data: clicks,
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: false,
          tension: 0.4,
          yAxisID: 'y1'
        },
        {
          label: 'Impressions',
          data: impressions,
          borderColor: 'rgb(249, 115, 22)',
          backgroundColor: 'rgba(249, 115, 22, 0.1)',
          fill: false,
          tension: 0.4,
          yAxisID: 'y1'
        }
      ]
    };
    
    setChartData(newChartData);
    console.log("DSA chart updated with new data", newChartData);
  };

  useEffect(() => {
    const fetchData = async () => {
      setDataLoading(true);
      try {
        // DSA Landing Page verilerini çek
        await fetchDsaLandingPageData();
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setDataLoading(false);
      }
    };

    fetchData();
  }, [dateRange, customStartDate, customEndDate, selectedAccountId, gaAccountFilter]);

  useEffect(() => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) return;
    fetch('https://api.glowytics.com/api/auth/connections', {
      credentials: 'include',
      headers: { 'X-Brand-ID': brandId }
    })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (data.connections || []);
        const ga = list.filter(c => c.platform === 'GOOGLE_ADS' && c.status === 'ACTIVE');
        setGaAccounts(ga);
      })
      .catch(() => {});
  }, []);

  // Normalize campaign status to 'ENABLED' or 'PAUSED'
  const cleanStatus = (s) => {
    if (!s) return '';
    const cleaned = s.replace('CampaignStatus.', '').trim().toUpperCase();
    if (cleaned === 'ACTIVE' || cleaned === 'ENABLED') return 'ENABLED';
    if (cleaned === 'PAUSED') return 'PAUSED';
    return cleaned;
  };

  // Update campaign status or budget via live Google Ads API
  const updateEntity = useCallback(async (entityType, entityId, field, value, campaignId, oldValue, entityName) => {
    setUpdatingId(campaignId);
    setOpenStatusDropdown(null);
    try {
      const response = await fetch('https://api.glowytics.com/api/labs/google-ads/campaigns-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        },
        body: JSON.stringify({ entityType, entityId, field, value, oldValue, entityName }),
        credentials: 'include'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.details?.error || 'Update failed');
      if (data.success) {
        toast.success('Updated successfully');
        setLiveDataMap(prev => {
          const key = String(campaignId);
          const existing = prev[key] || {};
          if (field === 'status') {
            return { ...prev, [key]: { ...existing, status: value } };
          } else if (field === 'amount_micros') {
            return { ...prev, [key]: { ...existing, budget: parseInt(value) / 1000000 } };
          }
          return prev;
        });
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUpdatingId(null);
      setEditBudget(null);
    }
  }, [selectedBrand?.id]);

  const handleSaveBudget = useCallback((campaignId, campaignName) => {
    if (!editBudget || editBudget.id !== campaignId) return;
    const newValue = parseFloat(editBudget.value);
    const micros = Math.round(newValue * 1000000);
    if (isNaN(micros) || micros <= 0) {
      toast.error('Invalid budget amount');
      return;
    }
    const live = liveDataMap[String(campaignId)];
    if (!live?.budgetId) {
      toast.error('Budget ID not found for this campaign');
      return;
    }
    const oldBudgetMicros = live.budget ? Math.round(live.budget * 1000000) : null;
    setConfirmDialog({
      title: 'Change Budget',
      message: `Are you sure you want to change the budget to ₺${newValue.toLocaleString('tr-TR')}?`,
      variant: 'budget',
      onConfirm: () => {
        updateEntity('budget', live.budgetId, 'amount_micros', micros, campaignId, oldBudgetMicros ? String(oldBudgetMicros) : null, campaignName || null);
        setConfirmDialog(null);
      }
    });
  }, [editBudget, liveDataMap, updateEntity]);

  // Fetch change history data
  const fetchChangeHistory = useCallback(async (offset = 0, append = false) => {
    if (!selectedBrand?.id) return;
    setChangeHistoryLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50', offset: String(offset) });
      if (changeHistoryFilter !== 'all') params.set('action', changeHistoryFilter);
      const response = await fetch(`https://api.glowytics.com/api/labs/ads-change-history?${params}`, {
        headers: { 'X-Brand-ID': selectedBrand.id },
        credentials: 'include'
      });
      const result = await response.json();
      if (result.success) {
        setChangeHistoryData(prev => append ? [...prev, ...result.data] : result.data);
        setChangeHistoryTotal(result.total);
      }
    } catch (err) {
      console.error('Failed to fetch change history:', err);
    } finally {
      setChangeHistoryLoading(false);
    }
  }, [selectedBrand?.id, changeHistoryFilter]);

  useEffect(() => {
    if (showChangeHistory) fetchChangeHistory(0);
  }, [showChangeHistory, fetchChangeHistory]);

  // Fetch live status + daily budget from Google Ads API
  const fetchLiveCampaignData = useCallback(async () => {
    if (!selectedBrand?.id) return;
    setIsLoadingLive(true);
    try {
      const response = await fetch('https://api.glowytics.com/api/labs/google-ads/campaigns-live', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        },
        credentials: 'include'
      });
      const data = await response.json();
      console.log('[DynamicSearch] campaigns-live response:', { ok: response.ok, success: data.success, count: data.campaigns?.length, error: data.error });
      if (response.ok && data.success && Array.isArray(data.campaigns)) {
        const map = {};
        data.campaigns.forEach(c => {
          if (c.id != null) {
            map[String(c.id)] = {
              id: c.id,
              status: c.status || null,
              budget: c.budget != null ? c.budget : null,
              budgetId: c.budgetId || null
            };
          }
        });
        console.log('[DynamicSearch] liveDataMap keys:', Object.keys(map));
        setLiveDataMap(map);
      } else {
        console.warn('[DynamicSearch] campaigns-live failed:', { status: response.status, error: data.error });
      }
    } catch (e) {
      console.error('[DynamicSearch] campaigns-live fetch error:', e);
    } finally {
      setIsLoadingLive(false);
    }
  }, [selectedBrand?.id]);

  // Fetch live data once on mount and whenever brand changes
  useEffect(() => {
    fetchLiveCampaignData();
  }, [fetchLiveCampaignData]);

  // Find and update the formatNumber function or any other function that formats currency values
  const formatNumber = (value, options = {}) => {
    if (value === undefined || value === null) return '0';
    
    const {
      prefix = '',
      suffix = '',
      minimumFractionDigits = 2,
      maximumFractionDigits = 2,
      notation = 'standard'
    } = options;
    
    // Remove any existing currency symbol from the value if it's a string
    let numericValue = value;
    if (typeof value === 'string') {
      numericValue = value.replace(/[₺$€£¥]/g, '');
    }
    
    // Convert to number
    numericValue = parseFloat(numericValue);
    
    // Format the number
    return `${prefix}${numericValue.toLocaleString(undefined, {
      minimumFractionDigits,
      maximumFractionDigits,
      notation
    })}${suffix}`;
  };

  // Get summary metrics from DSA landing page data - backend field names ile uyumlu
  const summaryMetrics = dsaLandingPageData?.totalMetrics || { 
    overall_cpc: 0, 
    overall_ctr: 0, 
    total_cost: 0, 
    total_clicks: 0,
    total_impressions: 0,
    unique_landing_pages: 0,
    unique_campaigns: 0
  };
  
  // Process DSA campaigns from landing page data
  const processedCampaigns = React.useMemo(() => {
    if (!dsaLandingPageData?.campaignSummary || dsaLandingPageData.campaignSummary.length === 0) {
      console.log('No DSA campaign summary data available');
      return [];
    }

    console.log('Processing DSA campaigns from backend data:', dsaLandingPageData.campaignSummary);

    return dsaLandingPageData.campaignSummary.map((campaign, index) => {
      console.log(`Processing campaign ${index}:`, campaign);
      
      // Backend field names ile uyumlu field mapping
      const processedCampaign = {
        id: campaign.campaign || `dsa-campaign-${index}`,
        name: campaign.campaign || `DSA Campaign ${index + 1}`,
        status: 'ENABLED', // DSA campaign status
        spend: parseFloat(campaign.cost || 0),
        budget: parseFloat(campaign.cost || 0) * 1.2, // Estimated budget 
        impressions: parseInt(campaign.impressions || 0),
        clicks: parseInt(campaign.clicks || 0),
        ctr: campaign.impressions > 0 ? ((campaign.clicks / campaign.impressions) * 100) : 0,
        cpc: campaign.clicks > 0 ? (campaign.cost / campaign.clicks) : 0,
        conversions: parseInt(campaign.conversions || 0),
        conversionRate: campaign.clicks > 0 ? ((campaign.conversions / campaign.clicks) * 100) : 0,
        costPerConversion: campaign.conversions > 0 ? (campaign.cost / campaign.conversions) : 0,
        conversionValue: parseFloat(campaign.conversion_value || 0),
        roas: campaign.cost > 0 ? (campaign.conversion_value / campaign.cost) : 0,
        // Ad Groups - Landing Page bazında gruplar oluşturabiliriz
        adGroups: dsaLandingPageData.landingPageSummary?.filter(lp => lp.campaign === campaign.campaign)?.map((landingPage, lpIndex) => {
          // URL'den sayfa adını çıkar
          let pageName = 'Page';
          try {
            if (landingPage.landing_page) {
              const url = new URL(landingPage.landing_page);
              pageName = url.pathname.split('/').pop() || url.pathname.split('/').slice(-2)[0] || 'Page';
            }
          } catch {
            if (landingPage.landing_page) {
              const parts = landingPage.landing_page.split('/');
              pageName = parts[parts.length - 1] || parts[parts.length - 2] || 'Page';
            }
          }
          
          return {
            id: `${campaign.campaign}-lp-${lpIndex}`,
            name: `Landing Page: ${pageName}`,
            status: 'ENABLED',
            spend: parseFloat(landingPage.cost || 0),
            impressions: parseInt(landingPage.impressions || 0),
            clicks: parseInt(landingPage.clicks || 0),
            ctr: landingPage.impressions > 0 ? ((landingPage.clicks / landingPage.impressions) * 100) : 0,
            cpc: landingPage.clicks > 0 ? (landingPage.cost / landingPage.clicks) : 0,
            conversions: parseInt(landingPage.conversions || 0),
            conversionRate: landingPage.clicks > 0 ? ((landingPage.conversions / landingPage.clicks) * 100) : 0,
            costPerConversion: landingPage.conversions > 0 ? (landingPage.cost / landingPage.conversions) : 0,
            conversionValue: parseFloat(landingPage.conversion_value || 0),
            roas: landingPage.cost > 0 ? (landingPage.conversion_value / landingPage.cost) : 0,
            keywords: [] // DSA'da keyword yok, auto-generated queries olur
          };
        }) || []
      };
      
      console.log(`Processed campaign ${index}:`, processedCampaign);
      return processedCampaign;
    });
  }, [dsaLandingPageData]);

  // useEffect içinde, sayfa yüklendiğinde varsayılan olarak budget'a göre sırala
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Veri çekme işlemleri...
        
        // Varsayılan olarak budget'a göre azalan sıralama yap
        setSortField('budget');
        setSortDirection('desc');
        
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dateRange]);

  // Kampanyaları sırala - DSA campaign data'sını kullan
  const sortedCampaigns = [...processedCampaigns].sort((a, b) => {
    // Sayısal değerler için sıralama
    if (['spend', 'budget', 'impressions', 'clicks', 'ctr', 'cpc', 'conversions', 'conversionRate', 'costPerConversion', 'conversionValue', 'roas'].includes(sortField)) {
      // Sayısal değerleri güvenli bir şekilde al
      const aValue = parseFloat(a[sortField]) || 0;
      const bValue = parseFloat(b[sortField]) || 0;
      
      return sortDirection === 'asc' 
        ? aValue - bValue 
        : bValue - aValue;
    }
    
    // Metin değerleri için sıralama (kampanya adı, durum vb.)
    if (['name', 'status', 'text'].includes(sortField)) {
      const aValue = String(a[sortField] || '').toLowerCase();
      const bValue = String(b[sortField] || '').toLowerCase();
      
      if (sortDirection === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    }
    
    // Varsayılan olarak budget'a göre azalan sıralama
    return b.budget - a.budget;
  });

  // Ad Group'ları sırala
  const sortAdGroups = (adGroups, field, direction) => {
    if (!field) return adGroups;

    return [...adGroups].sort((a, b) => {
      // Sayısal değerler için sıralama
      if (['spend', 'budget', 'impressions', 'clicks', 'ctr', 'cpc', 'conversions', 'conversionRate', 'costPerConversion', 'conversionValue', 'roas'].includes(field)) {
        // Sayısal değerleri güvenli bir şekilde al
        const aValue = parseFloat(a[field]) || 0;
        const bValue = parseFloat(b[field]) || 0;

        return direction === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }

      // Metin değerleri için sıralama (ad group adı, durum vb.)
      if (['name', 'status', 'text'].includes(field)) {
        const aValue = String(a[field] || '').toLowerCase();
        const bValue = String(b[field] || '').toLowerCase();
        
        if (direction === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }
      
      // Varsayılan olarak budget'a göre azalan sıralama
      return b.budget - a.budget;
    });
  };



  // Click-outside handler ekleyin
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDateDropdownOpen) {
        setIsDateDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isDateDropdownOpen]);

  return (
    <div className="p-6 space-y-6 bg-[#f6f8ff]/70">
      {/* Confirmation Dialog */}
      {confirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setConfirmDialog(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className={`px-6 pt-6 pb-4 flex items-start gap-4 ${
              confirmDialog.variant === 'pause' ? 'bg-orange-50/50' :
              confirmDialog.variant === 'enable' ? 'bg-green-50/50' :
              'bg-blue-50/50'
            }`}>
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                confirmDialog.variant === 'pause' ? 'bg-orange-100' :
                confirmDialog.variant === 'enable' ? 'bg-green-100' :
                'bg-blue-100'
              }`}>
                <AlertTriangle className={`w-5 h-5 ${
                  confirmDialog.variant === 'pause' ? 'text-orange-600' :
                  confirmDialog.variant === 'enable' ? 'text-green-600' :
                  'text-blue-600'
                }`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{confirmDialog.title}</h3>
                <p className="mt-1 text-sm text-gray-600">{confirmDialog.message}</p>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50/80 flex items-center justify-end gap-3">
              <button
                onClick={() => setConfirmDialog(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDialog.onConfirm}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors focus:outline-none focus:ring-2 ${
                  confirmDialog.variant === 'pause' ? 'bg-orange-500 hover:bg-orange-600 focus:ring-orange-200' :
                  confirmDialog.variant === 'enable' ? 'bg-green-500 hover:bg-green-600 focus:ring-green-200' :
                  'bg-blue-500 hover:bg-blue-600 focus:ring-blue-200'
                }`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center bg-white px-5 py-3 rounded-2xl shadow-sm w-full gap-3 md:gap-0 min-w-0">
        <div className="flex items-center gap-2.5 w-full md:w-auto">
          <img
            src="/search.svg"
            alt="Dynamic Search Ads"
            className="w-7 h-7 object-contain"
          />
          <h1 className="text-lg font-semibold text-gray-900">Dynamic Search Ads</h1>
        </div>

        <div className="flex flex-wrap items-center gap-3 justify-center md:justify-end w-full md:w-auto">
          {/* Account Switcher */}
          <AccountSwitcher platform="GOOGLE_ADS" />

          {/* Seçili tarih aralığı */}
          <div className="text-xs text-gray-500">
            {(() => {
              switch (dateRange) {
                case 'today':
                  return new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
                case 'yesterday':
                  const yesterday = new Date();
                  yesterday.setDate(yesterday.getDate() - 1);
                  return yesterday.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
                case 'last7':
                  const last7 = new Date();
                  last7.setDate(last7.getDate() - 7);
                  return `${last7.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'last30':
                  const last30 = new Date();
                  last30.setDate(last30.getDate() - 30);
                  return `${last30.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'thisMonth':
                  const firstDay = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
                  return `${firstDay.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'custom':
                  if (customStartDate && customEndDate) {
                    return `${customStartDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${customEndDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                  }
                  return 'Custom Range';
                default:
                  return '';
              }
            })()}
          </div>

          {/* Google Ads Account Filter */}
          {gaAccounts.length >= 2 && (
            <select
              value={gaAccountFilter}
              onChange={(e) => setGaAccountFilter(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-xl text-sm bg-white shadow-sm hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            >
              <option value="all">All Accounts</option>
              {gaAccounts.map(acc => (
                <option key={acc.customerId} value={acc.customerId}>
                  {acc.nickname || acc.accountName || acc.customerId}
                </option>
              ))}
            </select>
          )}

          {/* Unified date range filter (replaces legacy dropdown + modal) */}
          <DateRangeFilter
            value={dateRange}
            customStart={customStartDate}
            customEnd={customEndDate}
            onChange={(range, start, end) => {
              setDateRange(range);
              setCustomStartDate(start);
              setCustomEndDate(end);
            }}
            presets={['yesterday', 'last7', 'last30', 'thisMonth', 'lastMonth', 'custom']}
          />

        </div>
      </div>

      {/* Custom date modal removed — <DateRangeFilter /> owns the calendar popover now */}

      <Ga4WebOnlyNote />

      {/* Performance Overview */}
      <div className="grid grid-cols-12 gap-6">
        {/* Chart */}
        <Card className="col-span-8 rounded-2xl overflow-hidden bg-white">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Dynamic Search Performance</h2>
                <p className="text-sm text-gray-500 mt-1">Website content-targeted dynamic ads performance</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="h-[300px]">
              {dataLoading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="loading-logo-container">
                    <img 
                      src="/logoicon.png" 
                      alt="Glowytics Logo" 
                      className="loading-logo"
                    />
                  </div>
                </div>
              ) : (
                <Line data={chartData} options={chartOptions} />
              )}
            </div>
          </div>
        </Card>

        {/* Key Metrics - SearchCampaigns'deki gibi 5 satır yapıyoruz */}
        <div className="col-span-4 grid grid-cols-1 gap-3">
          {/* Row 1: Landing Pages + CTR */}
          <div className="grid grid-cols-2 gap-3">
            {/* Unique Landing Pages Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Landing Pages</p>
                <img src="/search.svg" alt="Dynamic Search" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">
                  {summaryMetrics.unique_landing_pages || 0}
                </h3>
                <p className="text-xs text-gray-500">Unique Pages</p>
              </div>
            </div>
            {/* CTR Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">CTR</p>
                <img src="/search.svg" alt="Dynamic Search" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">{(summaryMetrics.overall_ctr || 0).toFixed(2)}%</h3>
                <p className="text-xs text-gray-500">Click Rate</p>
              </div>
            </div>
          </div>

          {/* Row 2: Cost + Campaigns */}
          <div className="grid grid-cols-2 gap-3">
            {/* Cost Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Cost</p>
                <img src="/search.svg" alt="Dynamic Search" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">₺{Math.floor(summaryMetrics.total_cost || 0).toLocaleString('tr-TR')}</h3>
                <p className="text-xs text-gray-500">Total Spend</p>
              </div>
            </div>
            {/* Campaigns Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Campaigns</p>
                <img src="/search.svg" alt="Dynamic Search" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">{summaryMetrics.unique_campaigns || 0}</h3>
                <p className="text-xs text-gray-500">Active</p>
              </div>
            </div>
          </div>

          {/* Row 3: Clicks + Impressions */}
          <div className="grid grid-cols-2 gap-3">
            {/* Clicks Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Clicks</p>
                <img src="/search.svg" alt="Dynamic Search" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">{(summaryMetrics.total_clicks || 0).toLocaleString()}</h3>
                <p className="text-xs text-gray-500">Total Clicks</p>
              </div>
            </div>
            {/* Impressions Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Impressions</p>
                <img src="/search.svg" alt="Dynamic Search" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">{(summaryMetrics.total_impressions || 0).toLocaleString()}</h3>
                <p className="text-xs text-gray-500">Total Views</p>
              </div>
            </div>
          </div>

          {/* Row 4: CPC + Top Landing Page */}
          <div className="grid grid-cols-2 gap-3">
            {/* CPC Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Avg CPC</p>
                <img src="/search.svg" alt="Dynamic Search" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">₺{(summaryMetrics.overall_cpc || 0).toFixed(2)}</h3>
                <p className="text-xs text-gray-500">Per Click</p>
              </div>
            </div>
            {/* Top Landing Page Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Top Page</p>
                <img src="/search.svg" alt="Dynamic Search" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-sm font-bold text-gray-800 truncate">
                  {dsaLandingPageData?.landingPageSummary?.[0]?.landing_page ? 
                    (() => {
                      try {
                        return new URL(dsaLandingPageData.landingPageSummary[0].landing_page).pathname.split('/').pop() || 'N/A';
                      } catch {
                        return dsaLandingPageData.landingPageSummary[0].landing_page.split('/').pop() || 'N/A';
                      }
                    })()
                    : 'N/A'}
                </h3>
                <p className="text-xs text-gray-500">Best Performer</p>
              </div>
            </div>
          </div>

          {/* Row 5: GA4 Metrics */}
          <div className="grid grid-cols-2 gap-3">
            {/* GA4 Sessions Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Sessions</p>
                <img src="/GA4logoicon.svg" alt="GA4" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">0</h3>
                <p className="text-xs text-gray-500">GA4 Data</p>
              </div>
            </div>
            {/* GA4 Revenue Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">GA4 Revenue</p>
                <img src="/GA4logoicon.svg" alt="GA4" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">₺0</h3>
                <p className="text-xs text-gray-500">0x ROAS</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Campaign Performance Table with Ad Groups */}
      <Card className="rounded-2xl overflow-hidden bg-white">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Dynamic Search Campaigns</h2>
              <p className="text-sm text-gray-500 mt-1">Detailed metrics for dynamic search ads campaigns and ad groups</p>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          {dataLoading ? (
            <div className="min-h-[400px] flex items-center justify-center">
              <div className="loading-logo-container">
                <img 
                  src="/logoicon.png" 
                  alt="Glowytics Logo" 
                  className="loading-logo"
                />
              </div>
            </div>
          ) : processedCampaigns.length === 0 ? (
            <div className="flex justify-center items-center p-12">
              <div className="text-center">
                <p className="text-gray-600 mb-2">No DSA campaign data available</p>
                <p className="text-sm text-gray-500">Connect your Google Ads account to see Dynamic Search Ads performance</p>
              </div>
            </div>
          ) : (
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th
                    className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('text')}
                  >
                    Campaign / Ad Group
                    {sortField === 'text' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('budget')}
                  >
                    Budget
                    {sortField === 'budget' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('spend')}
                  >
                    Cost
                    {sortField === 'spend' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('impressions')}
                  >
                    Impr.
                    {sortField === 'impressions' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('clicks')}
                  >
                    Clicks
                    {sortField === 'clicks' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('ctr')}
                  >
                    CTR
                    {sortField === 'ctr' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('cpc')}
                  >
                    Avg. CPC
                    {sortField === 'cpc' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('conversions')}
                  >
                    Conv.
                    {sortField === 'conversions' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('conversionRate')}
                  >
                    Conv. Rate
                    {sortField === 'conversionRate' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('costPerConversion')}
                  >
                    Cost/Conv.
                    {sortField === 'costPerConversion' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('conversionValue')}
                  >
                    Revenue
                    {sortField === 'conversionValue' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('roas')}
                  >
                    ROAS
                    {sortField === 'roas' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedCampaigns.map((campaign) => (
                  <React.Fragment key={campaign.id}>
                    {/* Campaign Row */}
                    <tr
                      className={`group/row hover:bg-blue-50/30 cursor-pointer transition-colors ${
                        expandedCampaigns.has(campaign.id) ? 'bg-blue-50/50 font-semibold' : 'bg-white'
                      }`}
                      onClick={(e) => toggleCampaign(campaign.id, e)}
                    >
                      <td className="py-2.5 px-3 border-r border-gray-200 relative">
                        <div className="flex items-center gap-2">
                          {/* Expansion Icon */}
                          <div className="flex items-center justify-center w-5 h-5">
                            {expandedCampaigns.has(campaign.id) ?
                              <ChevronDown className="w-4 h-4 text-blue-500" /> :
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            }
                          </div>
                          {/* Status dot + hover arrow + Campaign Name */}
                          {(() => {
                            const live = liveDataMap[String(campaign.id)];
                            const resolvedStatus = cleanStatus(live?.status || campaign.status || '');
                            const liveId = live?.id || campaign.id;
                            return (
                              <>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setOpenStatusDropdown(openStatusDropdown === campaign.id ? null : campaign.id); }}
                                  disabled={updatingId === campaign.id}
                                  className="flex-shrink-0 -ml-1 flex items-center gap-0.5 px-1 py-1 rounded-md hover:bg-gray-100 transition-colors focus:outline-none group/dot"
                                  title={resolvedStatus === 'ENABLED' ? 'Enabled — click to change' : 'Paused — click to change'}
                                >
                                  {updatingId === campaign.id ? (
                                    <Loader2 className="w-2.5 h-2.5 animate-spin text-gray-500" />
                                  ) : (
                                    <div className={`w-2.5 h-2.5 rounded-full ${resolvedStatus === 'ENABLED' ? 'bg-green-500 shadow-[0_0_0_3px_rgba(34,197,94,0.15)]' : 'bg-gray-400 shadow-[0_0_0_3px_rgba(156,163,175,0.15)]'}`} />
                                  )}
                                  <ChevronDown className="w-3 h-3 text-gray-400 opacity-0 group-hover/row:opacity-100 transition-opacity" />
                                </button>
                                {openStatusDropdown === campaign.id && (
                                  <>
                                    <div className="fixed inset-0 z-10 cursor-default" onClick={() => setOpenStatusDropdown(null)} />
                                    <div className="absolute top-full left-8 mt-1 w-40 bg-white rounded-xl shadow-xl border border-gray-100 z-20 py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-100 origin-top-left">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOpenStatusDropdown(null);
                                          setConfirmDialog({
                                            title: 'Enable Campaign',
                                            message: `Are you sure you want to enable campaign "${campaign.name}"?`,
                                            variant: 'enable',
                                            onConfirm: () => {
                                              updateEntity('campaign', liveId, 'status', 'ENABLED', campaign.id, resolvedStatus, campaign.name);
                                              setConfirmDialog(null);
                                            }
                                          });
                                        }}
                                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 transition-colors
                                          ${resolvedStatus === 'ENABLED' ? 'text-green-600 bg-green-50/50' : 'text-gray-700'}
                                        `}
                                      >
                                        <div className="w-2 h-2 rounded-full bg-green-500" />
                                        <span>Enable</span>
                                        {resolvedStatus === 'ENABLED' && <Check className="w-3.5 h-3.5 ml-auto" />}
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOpenStatusDropdown(null);
                                          setConfirmDialog({
                                            title: 'Pause Campaign',
                                            message: `Are you sure you want to pause campaign "${campaign.name}"?`,
                                            variant: 'pause',
                                            onConfirm: () => {
                                              updateEntity('campaign', liveId, 'status', 'PAUSED', campaign.id, resolvedStatus, campaign.name);
                                              setConfirmDialog(null);
                                            }
                                          });
                                        }}
                                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 transition-colors
                                          ${resolvedStatus === 'PAUSED' ? 'text-gray-900 bg-gray-50' : 'text-gray-700'}
                                        `}
                                      >
                                        <div className="w-2 h-2 rounded-full bg-gray-400" />
                                        <span>Pause</span>
                                        {resolvedStatus === 'PAUSED' && <Check className="w-3.5 h-3.5 ml-auto" />}
                                      </button>
                                    </div>
                                  </>
                                )}
                                {/* Campaign icon + name */}
                                <div className="w-6 h-6 bg-transparent flex items-center justify-center flex-shrink-0">
                                  <img
                                    src="/search.svg"
                                    alt="Dynamic Search Campaign"
                                    className="w-4 h-4"
                                  />
                                </div>
                                <p className="text-[13px] text-gray-900">{campaign.name}</p>
                              </>
                            );
                          })()}
                        </div>
                      </td>
                      {/* Budget cell with inline edit */}
                      <td className="px-3 py-2.5 whitespace-nowrap text-right border-r border-gray-200" onClick={e => e.stopPropagation()}>
                        {(() => {
                          const live = liveDataMap[String(campaign.id)];
                          const isEditing = editBudget?.id === campaign.id;
                          const displayBudget = live?.budget != null ? live.budget : campaign.budget;
                          const hasBudgetId = !!(live?.budgetId || campaign.budget_id);

                          if (isEditing) {
                            return (
                              <div className="flex items-center justify-end gap-2">
                                <input
                                  type="number"
                                  value={editBudget.value}
                                  onChange={(e) => setEditBudget({ ...editBudget, value: e.target.value })}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveBudget(campaign.id, campaign.name);
                                    if (e.key === 'Escape') setEditBudget(null);
                                  }}
                                  className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                                  autoFocus
                                />
                                <button onClick={() => handleSaveBudget(campaign.id, campaign.name)} className="p-1 text-green-600 hover:bg-green-50 rounded">
                                  <Check className="w-4 h-4" />
                                </button>
                                <button onClick={() => setEditBudget(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            );
                          }

                          return (
                            <div className="flex items-center justify-end gap-2 group">
                              <span className="text-sm text-gray-900 font-medium">
                                {displayBudget != null ? `₺${displayBudget.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : isLoadingLive ? <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 inline" /> : '—'}
                              </span>
                              {hasBudgetId && (
                                <button
                                  onClick={() => setEditBudget({ id: campaign.id, value: displayBudget || 0 })}
                                  disabled={updatingId === campaign.id}
                                  className="opacity-0 group-hover/row:opacity-100 p-1 text-gray-400 hover:text-blue-600 transition-opacity"
                                >
                                  <Edit2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">
                        ₺{formatNumber(campaign.spend, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.impressions || 0).toLocaleString()}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.clicks || 0).toLocaleString()}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{isNaN(campaign.ctr) ? '0.00' : campaign.ctr.toFixed(2)}%</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{isNaN(campaign.cpc) ? '0.00' : campaign.cpc.toFixed(2)}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{campaign.conversions || 0}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{isNaN(campaign.conversionRate) ? '0.00' : campaign.conversionRate.toFixed(2)}%</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{isNaN(campaign.costPerConversion) ? '0.00' : campaign.costPerConversion.toFixed(2)}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{(campaign.conversionValue || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                      <td className="text-right py-2.5 px-3 text-[13px] font-medium text-gray-900">{isNaN(campaign.roas) ? '0.00' : campaign.roas.toFixed(2)}x</td>
                    </tr>

                    {/* Ad Groups Section */}
                    {expandedCampaigns.has(campaign.id) && campaign.adGroups && (
                      <>
                        {sortAdGroups(campaign.adGroups, sortField, sortDirection).map((adGroup) => (
                          <React.Fragment key={adGroup.id}>
                            {/* Ad Group Row */}
                            <tr
                              className="hover:bg-blue-50/20 bg-blue-50/5"
                              onClick={(e) => toggleAdGroup(adGroup.id, e)}
                            >
                              <td className="py-2 px-3 border-r border-gray-200">
                                <div className="flex items-center gap-2 pl-[2.75rem]">
                                  <div className="flex items-center justify-center w-5 h-5">
                                    {expandedAdGroups.has(adGroup.id) ?
                                      <ChevronDown className="w-4 h-4 text-blue-500" /> :
                                      <ChevronRight className="w-4 h-4 text-gray-400" />
                                    }
                                  </div>
                                  <div className="w-5 h-5 flex items-center justify-center">
                                    <img
                                      src="/enableicon.svg"
                                      alt="Ad Group"
                                      className="w-3 h-3"
                                    />
                                  </div>
                                  <div>
                                    <p className="text-[13px] font-medium text-gray-800">{adGroup.name}</p>
                                  </div>
                                </div>
                              </td>
                              {/* Budget — dash for ad groups */}
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] text-gray-400">—</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{formatNumber(adGroup.spend)}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{(adGroup.impressions || 0).toLocaleString()}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{(adGroup.clicks || 0).toLocaleString()}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{isNaN(adGroup.ctr) ? '0.00' : adGroup.ctr.toFixed(2)}%</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{isNaN(adGroup.cpc) ? '0.00' : adGroup.cpc.toFixed(2)}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{adGroup.conversions || 0}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{isNaN(adGroup.conversionRate) ? '0.00' : adGroup.conversionRate.toFixed(2)}%</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{isNaN(adGroup.costPerConversion) ? '0.00' : adGroup.costPerConversion.toFixed(2)}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{(adGroup.conversionValue || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                              <td className="text-right py-2 px-3 text-[13px] font-medium text-gray-800">{isNaN(adGroup.roas) ? '0.00' : adGroup.roas.toFixed(2)}x</td>
                            </tr>

                            {/* Keywords Section */}
                            {expandedAdGroups.has(adGroup.id) && adGroup.keywords && sortKeywords(adGroup.keywords, sortField, sortDirection).map((keyword) => (
                              <tr 
                                key={keyword.id}
                                className="hover:bg-blue-50/10 bg-blue-50/2"
                              >
                                <td className="py-2 px-3 border-r border-gray-200">
                                  <div className="flex items-center gap-2 pl-[5.5rem]">
                                    {/* 'keyword.text' yerine 'keyword.text || "Placement/Target"' kullanarak hem keyword hem placement verilerini gösterebiliriz */}
                                    <p className="text-[12px] font-medium text-gray-700">{keyword.text}</p>
                                    <div className="flex gap-1">
                                      {/* Eşleşme tipi yerine yerleşim türünü (website, video, app vb.) gösterebiliriz */}
                                      {keyword.matchTypes && keyword.matchTypes.map((matchType, index) => (
                                        <span key={index} className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                          matchType.includes('EXACT') ? 'bg-blue-100 text-blue-700' :
                                          matchType.includes('PHRASE') ? 'bg-green-100 text-green-700' :
                                          matchType.includes('BROAD') ? 'bg-purple-100 text-purple-700' :
                                          'bg-gray-100 text-gray-600'
                                        }`}>
                                          {/* Match type değiştirildi */}
                                          {matchType.replace('KeywordMatchType.', '').replace('KEYWORD_MATCH_TYPE_', '')}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </td>
                                {/* Budget — dash for keywords */}
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] text-gray-400">—</td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">₺{formatNumber(keyword.spend)}</td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">{(keyword.impressions || 0).toLocaleString()}</td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">{(keyword.clicks || 0).toLocaleString()}</td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">
                                  {keyword.impressions > 0 ? ((keyword.clicks / keyword.impressions) * 100).toFixed(2) : '0.00'}%
                                </td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">
                                  ₺{formatNumber(keyword.clicks > 0 ? formatNumber((keyword.spend / keyword.clicks).toFixed(2)) : '0.00')}
                                </td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">{keyword.conversions || 0}</td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">
                                  {keyword.clicks > 0 ? ((keyword.conversions / keyword.clicks) * 100).toFixed(2) : '0.00'}%
                                </td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">
                                  ₺{formatNumber(keyword.conversions > 0 ? formatNumber((keyword.spend / keyword.conversions).toFixed(2)) : '0.00')}
                                </td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">
                                  ₺{(keyword.conversionValue || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                                <td className="text-right py-2 px-3 text-[12px] font-medium text-gray-700">
                                  {formatNumber(keyword.spend > 0 ? formatNumber((keyword.conversionValue / keyword.spend).toFixed(2)) : '0.00')}x
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
              {/* Toplam Satırı - Tüm kampanyaların toplamı */}
              <tfoot>
                <tr className="bg-gray-100 border-t-2 border-gray-300">
                  <td className="py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    Total
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 text-[13px] text-gray-400">
                    —
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    ₺{formatNumber(processedCampaigns.reduce((total, campaign) => total + (campaign.spend || 0), 0))}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {processedCampaigns.reduce((total, campaign) => total + (campaign.impressions || 0), 0).toLocaleString()}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {processedCampaigns.reduce((total, campaign) => total + (campaign.clicks || 0), 0).toLocaleString()}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {(() => {
                      const totalImpressions = processedCampaigns.reduce((total, campaign) => total + (campaign.impressions || 0), 0);
                      const totalClicks = processedCampaigns.reduce((total, campaign) => total + (campaign.clicks || 0), 0);
                      return totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '0.00';
                    })()}%
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    ₺{(() => {
                      const totalClicks = processedCampaigns.reduce((total, campaign) => total + (campaign.clicks || 0), 0);
                      const totalSpend = processedCampaigns.reduce((total, campaign) => total + (campaign.spend || 0), 0);
                      return totalClicks > 0 ? (totalSpend / totalClicks).toFixed(2) : '0.00';
                    })()}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {processedCampaigns.reduce((total, campaign) => total + (campaign.conversions || 0), 0).toLocaleString()}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {(() => {
                      const totalClicks = processedCampaigns.reduce((total, campaign) => total + (campaign.clicks || 0), 0);
                      const totalConversions = processedCampaigns.reduce((total, campaign) => total + (campaign.conversions || 0), 0);
                      return totalClicks > 0 ? ((totalConversions / totalClicks) * 100).toFixed(2) : '0.00';
                    })()}%
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    ₺{(() => {
                      const totalConversions = processedCampaigns.reduce((total, campaign) => total + (campaign.conversions || 0), 0);
                      const totalSpend = processedCampaigns.reduce((total, campaign) => total + (campaign.spend || 0), 0);
                      return totalConversions > 0 ? (totalSpend / totalConversions).toFixed(2) : '0.00';
                    })()}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    ₺{formatNumber(processedCampaigns.reduce((total, campaign) => total + (campaign.conversionValue || 0), 0))}
                  </td>
                  <td className="text-right py-3 px-3 font-medium text-[13px] text-gray-900">
                    {(() => {
                      const totalSpend = processedCampaigns.reduce((total, campaign) => total + (campaign.spend || 0), 0);
                      const totalConversionValue = processedCampaigns.reduce((total, campaign) => total + (campaign.conversionValue || 0), 0);
                      return totalSpend > 0 ? (totalConversionValue / totalSpend).toFixed(2) : '0.00';
                    })()}x
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </Card>
      
      {/* Top Keywords Card'ı, Top Placements Card'ı olarak değiştirildi */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Card className="rounded-2xl overflow-hidden bg-white">
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-medium text-gray-800">Top Auto Targets</h3>
              <select className="text-sm border-0 bg-gray-50 rounded-lg px-3 py-1.5">
                <option value="clicks">By Clicks</option>
                <option value="impressions">By Impr.</option>
                <option value="cost">By Cost</option>
                <option value="conversions">By Conv.</option>
              </select>
            </div>
          </div>
          
          <div className="flex flex-col items-center justify-center py-8">
            <div className="text-gray-400 mb-2">
              <Search className="w-12 h-12 mx-auto mb-2" />
            </div>
            <h3 className="text-lg font-medium text-gray-700 mb-1">No data available</h3>
            <p className="text-sm text-gray-500 text-center max-w-md">
              No auto targets data found for the selected date range. Please try a different date range or check back later.
            </p>
          </div>
        </Card>
        
        {/* İkinci kart - Landing Pages */}
        <Card className="rounded-2xl overflow-hidden bg-white">
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-medium text-gray-800">Top Landing Pages</h3>
              <select className="text-sm border-0 bg-gray-50 rounded-lg px-3 py-1.5">
                <option value="clicks">By Clicks</option>
                <option value="conversions">By Conv.</option>
                <option value="revenue">By Revenue</option>
                <option value="ctr">By CTR</option>
              </select>
            </div>
          </div>
          
          {isLoadingDsaData ? (
            <div className="flex items-center justify-center py-8">
              <div className="loading-logo-container">
                <img 
                  src="/logoicon.png" 
                  alt="Glowytics Logo" 
                  className="loading-logo"
                />
              </div>
            </div>
          ) : dsaLandingPageData?.landingPageSummary && dsaLandingPageData.landingPageSummary.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {dsaLandingPageData.landingPageSummary.slice(0, 10).map((landingPage, index) => {
                // Backend'te landing_page field'ı kullanılıyor
                const urlField = landingPage.landing_page || 'Unknown URL';
                
                console.log(`Landing page ${index + 1} URL field:`, urlField);
                console.log('All fields:', Object.keys(landingPage));
                
                // URL'den sadece domain ve path'i al
                let displayUrl = urlField;
                try {
                  if (urlField && urlField !== 'Unknown URL') {
                    const url = new URL(urlField);
                    displayUrl = url.hostname + url.pathname;
                    if (displayUrl.length > 40) {
                      displayUrl = displayUrl.substring(0, 37) + '...';
                    }
                  }
                } catch (e) {
                  // URL parse edilemezse olduğu gibi bırak
                  console.log('URL parse error:', e);
                  if (displayUrl.length > 40) {
                    displayUrl = displayUrl.substring(0, 37) + '...';
                  }
                }

                return (
                  <div key={index} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-600">{index + 1}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate" title={urlField}>
                            {displayUrl}
                          </p>
                          <div className="flex items-center gap-4 mt-1">
                            <span className="text-xs text-gray-500">
                              {(landingPage.clicks || 0).toLocaleString()} clicks
                            </span>
                            <span className="text-xs text-gray-500">
                              {(landingPage.impressions || 0).toLocaleString()} impr.
                            </span>
                            <span className="text-xs text-gray-500">
                              {landingPage.impressions > 0 ? 
                                ((landingPage.clicks / landingPage.impressions) * 100).toFixed(2) : 
                                '0.00'}% CTR
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-4">
                        <p className="text-sm font-semibold text-gray-900">
                          ₺{(landingPage.cost || 0).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(landingPage.conversions || 0)} conv.
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="text-gray-400 mb-2">
                <Search className="w-12 h-12 mx-auto mb-2" />
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-1">No landing pages available</h3>
              <p className="text-sm text-gray-500 text-center max-w-md">
                No DSA landing pages data found for the selected date range. Please try a different date range or check back later.
              </p>
            </div>
          )}
        </Card>
      </div>

      {/* Change History Slide-over */}
      {showChangeHistory && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowChangeHistory(false)} />
          <div className="relative w-full max-w-lg bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center">
                  <History className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Change History</h2>
                  <p className="text-xs text-gray-500">{changeHistoryTotal} total changes</p>
                </div>
              </div>
              <button onClick={() => setShowChangeHistory(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="px-6 py-3 border-b border-gray-50 flex gap-2">
              {[
                { key: 'all', label: 'All' },
                { key: 'status_change', label: 'Status' },
                { key: 'budget_change', label: 'Budget' }
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setChangeHistoryFilter(f.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    changeHistoryFilter === f.key
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {changeHistoryLoading && changeHistoryData.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
                </div>
              ) : changeHistoryData.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No changes recorded yet</p>
                  <p className="text-xs text-gray-400 mt-1">Changes to campaigns will appear here</p>
                </div>
              ) : (
                <>
                  {changeHistoryData.map((item, idx) => {
                    const isStatus = item.action === 'status_change';
                    const isBudget = item.action === 'budget_change';
                    const timeAgo = (() => {
                      const diff = Date.now() - new Date(item.created_at).getTime();
                      const mins = Math.floor(diff / 60000);
                      if (mins < 1) return 'Just now';
                      if (mins < 60) return `${mins}m ago`;
                      const hours = Math.floor(mins / 60);
                      if (hours < 24) return `${hours}h ago`;
                      const days = Math.floor(hours / 24);
                      if (days < 7) return `${days}d ago`;
                      return new Date(item.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
                    })();
                    const formatValue = (val, field) => {
                      if (!val) return '—';
                      if (field === 'amount_micros' || field === 'daily_budget') {
                        const num = parseFloat(val) / 1000000;
                        return `₺${num.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
                      }
                      return val;
                    };
                    return (
                      <div key={item.id || idx} className={`p-3.5 rounded-xl border transition-colors ${item.success ? 'border-gray-100 bg-white hover:bg-gray-50/50' : 'border-red-100 bg-red-50/30'}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mt-0.5 ${
                              !item.success ? 'bg-red-100' : isStatus ? 'bg-amber-100' : isBudget ? 'bg-blue-100' : 'bg-gray-100'
                            }`}>
                              {!item.success ? <XCircle className="w-4 h-4 text-red-500" /> :
                               isStatus ? <Activity className="w-4 h-4 text-amber-600" /> :
                               <DollarSign className="w-4 h-4 text-blue-600" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider ${
                                  isStatus ? 'bg-amber-100 text-amber-700' : isBudget ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {isStatus ? 'Status' : isBudget ? 'Budget' : item.action}
                                </span>
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  item.platform === 'GOOGLE_ADS' ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'
                                }`}>
                                  {item.platform === 'GOOGLE_ADS' ? 'Google' : 'Meta'}
                                </span>
                              </div>
                              {item.entity_name && (
                                <p className="text-sm font-medium text-gray-800 mt-1 truncate">{item.entity_name}</p>
                              )}
                              <div className="flex items-center gap-1.5 mt-1 text-xs">
                                <span className="text-gray-400">{formatValue(item.old_value, item.field_name)}</span>
                                <span className="text-gray-300">→</span>
                                <span className={`font-medium ${item.success ? 'text-gray-700' : 'text-red-600'}`}>
                                  {formatValue(item.new_value, item.field_name)}
                                </span>
                              </div>
                              {!item.success && item.error_message && (
                                <p className="text-xs text-red-500 mt-1 truncate">{item.error_message}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <div className="flex items-center gap-1 text-[11px] text-gray-400">
                              <Clock className="w-3 h-3" />
                              {timeAgo}
                            </div>
                            {item.user_email && (
                              <p className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[120px]">{item.user_name || item.user_email}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {changeHistoryData.length < changeHistoryTotal && (
                    <button
                      onClick={() => fetchChangeHistory(changeHistoryData.length, true)}
                      disabled={changeHistoryLoading}
                      className="w-full py-2.5 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {changeHistoryLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      Load More ({changeHistoryTotal - changeHistoryData.length} remaining)
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}